/**
 * React Query hooks for categorization rules management (WP-CAT-12)
 *
 * Provides hooks for:
 * - Fetching rules list
 * - Creating, updating, deleting rules
 * - Testing rules against sample text
 *
 * Pattern: Follows useCategoryPreferences.ts query key factory pattern
 * API Source: services/core/app/api/v1/categorization_rules.py
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categorizationRulesService } from '@/services/categorizationRules';
import type {
  CategorizationRule,
  RuleCreateRequest,
  RuleUpdateRequest,
  RuleTestRequest,
  RuleListParams,
} from '@/types/categorizationRule';

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query keys for categorization rules.
 *
 * Key structure:
 * - ['categorization-rules'] - all rule queries
 * - ['categorization-rules', 'list', params] - list with filters
 * - ['categorization-rules', 'detail', id] - single rule
 */
export const categorizationRuleKeys = {
  all: ['categorization-rules'] as const,
  lists: () => [...categorizationRuleKeys.all, 'list'] as const,
  list: (params: RuleListParams) => [...categorizationRuleKeys.lists(), params] as const,
  details: () => [...categorizationRuleKeys.all, 'detail'] as const,
  detail: (id: string) => [...categorizationRuleKeys.details(), id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all visible categorization rules (system + tenant-specific).
 *
 * @param params - Optional filter parameters
 * @returns Query result with rules list
 *
 * API: GET /categorization-rules
 */
export function useCategorizationRules(params: RuleListParams = {}) {
  return useQuery({
    queryKey: categorizationRuleKeys.list(params),
    queryFn: () => categorizationRulesService.getAll(params),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Fetch a single categorization rule by ID.
 *
 * @param id - Rule UUID
 * @returns Query result with rule details
 *
 * API: GET /categorization-rules/{rule_id}
 */
export function useCategorizationRule(id: string) {
  return useQuery({
    queryKey: categorizationRuleKeys.detail(id),
    queryFn: () => categorizationRulesService.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new custom categorization rule.
 *
 * Invalidates rule list queries on success.
 *
 * API: POST /categorization-rules
 */
export function useCreateCategorizationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RuleCreateRequest) => categorizationRulesService.create(data),

    onSuccess: () => {
      // Invalidate all rule list queries
      queryClient.invalidateQueries({ queryKey: categorizationRuleKeys.lists() });
    },
  });
}

/**
 * Update an existing custom categorization rule.
 *
 * Includes optimistic update for immediate UI feedback.
 *
 * API: PATCH /categorization-rules/{rule_id}
 */
export function useUpdateCategorizationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RuleUpdateRequest }) =>
      categorizationRulesService.update(id, data),

    // Optimistic update
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: categorizationRuleKeys.detail(id) });

      // Snapshot previous value
      const previousRule = queryClient.getQueryData<CategorizationRule>(
        categorizationRuleKeys.detail(id)
      );

      // Optimistically update detail query
      if (previousRule) {
        queryClient.setQueryData<CategorizationRule>(
          categorizationRuleKeys.detail(id),
          { ...previousRule, ...data }
        );
      }

      return { previousRule };
    },

    // Rollback on error
    onError: (_err, { id }, context) => {
      if (context?.previousRule) {
        queryClient.setQueryData(
          categorizationRuleKeys.detail(id),
          context.previousRule
        );
      }
    },

    // Invalidate on settle
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: categorizationRuleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categorizationRuleKeys.detail(id) });
    },
  });
}

/**
 * Delete a custom categorization rule.
 *
 * Removes from cache and invalidates list queries on success.
 *
 * API: DELETE /categorization-rules/{rule_id}
 */
export function useDeleteCategorizationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categorizationRulesService.delete(id),

    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: categorizationRuleKeys.detail(id) });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: categorizationRuleKeys.lists() });
    },
  });
}

/**
 * Test categorization rules against sample text.
 *
 * Returns all matching rules and the best match.
 *
 * API: POST /categorization-rules/test
 */
export function useTestCategorizationRules() {
  return useMutation({
    mutationFn: (data: RuleTestRequest) => categorizationRulesService.test(data),
  });
}

/**
 * Toggle a rule's active status.
 *
 * Convenience hook that wraps useUpdateCategorizationRule.
 */
export function useToggleCategorizationRuleActive() {
  const updateMutation = useUpdateCategorizationRule();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateMutation.mutateAsync({ id, data: { isActive } }),
  });
}
